/// <amd-module name="Build/Admin/Pool/Components/PoolGraph" />
import { AnalyticsHeaderComponent } from "Analytics/Shared/Common/AnalyticsHeaderComponent";
import { AnalyticsPage } from "Analytics/Shared/Common/AnalyticsPage";
import { ITrendReportConfiguration, Period, TrendBy } from "Analytics/Shared/Common/Types";
import { Telemetry } from "Build/Admin/Common/Constants";
import { PoolGraphActionsCreator } from "Build/Admin/Pool/Actions/PoolGraphActionCreator";
import { PoolTrendCard } from "Build/Admin/Pool/Components/PoolTrendCard";
import * as Resources from "Build/Admin/Pool/Resources";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { DropdownFilterBarItem } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { getFilterItem, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IListBoxItem } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { Filter } from "VSSUI/Utilities/Filter";

export interface IPoolGraphProps extends IVssComponentProperties {
    actionCreator: PoolGraphActionsCreator;
    storesHub: StoresHub;
}

export interface IPoolGraphState {
    errorText?: string;
    confValues: ITrendReportConfiguration;
    isHosted: boolean;
}

export class PoolGraph extends VssComponent<IPoolGraphProps, IPoolGraphState> {
    private _actionsCreator: PoolGraphActionsCreator;
    private _storesHub: StoresHub;

    private _hasPrivateTrendRendered = false;
    private _hasPublicTrendRendered = false;
    private _hasTopPipelinesRendered = false;
    private _reportRenderCount = 0;

    private _filter: Filter;
    private _timeRangeSelection = new DropdownSelection();
    private _periods: IListBoxItem<Period>[] = [];
    private _showFilter = new ObservableValue<boolean>(false);

    constructor(props: IPoolGraphProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            confValues: { period: Period.Days_14, trendBy: TrendBy.Days },
            isHosted: props.storesHub.selectionStore.state.selectedAgentPool?.isHosted!
        } as IPoolGraphState;
        this._actionsCreator = props.actionCreator;
        this._storesHub = props.storesHub;

        this._filter = new Filter();

        this._periods.push({ id: "Days_1", text: Resources.OneDay, data: Period.Days_1 });
        this._periods.push({ id: "Days_7", text: Resources.SevenDays, data: Period.Days_7 });
        this._periods.push({ id: "Days_14", text: Resources.FourteenDays, data: Period.Days_14 });
        this._periods.push({ id: "Days_30", text: Resources.ThirtyDays, data: Period.Days_30 });

        // Select 14 days as the period and set that as default
        this._timeRangeSelection.select(2);
        this._filter.setFilterItemState("timeRangeFilter", { value: [2] });
        this._filter.setDefaultState(this._filter.getState());
    }

    public componentDidMount(): void {
        this.props.storesHub.selectionStore.addChangedListener(this._onChanged);
        this._filter.subscribe(this.filterChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.selectionStore.removeChangedListener(this._onChanged);
        this._filter.unsubscribe(this.filterChanged);
        super.componentWillUnmount();
    }

    public render() {
        return (
            <div className="relative flex-row flex-grow">
                <AnalyticsPage
                    renderHeader={this._renderHeader}
                    getDataValidationPromises={this._getDataValidationPromises}
                    onError={this._handleError}
                >
                    <div className="flex-grow flex-column scroll-hidden">
                        <ConditionalChildren renderChildren={this._showFilter}>
                            <div className="margin-bottom-16">
                                <FilterBar filter={this._filter}>
                                    <DropdownFilterBarItem
                                        filterItemKey="timeRangeFilter"
                                        filter={this._filter}
                                        hideClearAction={true}
                                        items={this._periods}
                                        onSelect={() => this._getDataValidationPromises()}
                                        selection={this._timeRangeSelection}
                                    />
                                </FilterBar>
                            </div>
                        </ConditionalChildren>
                        <PoolTrendCard
                            className="flex-column"
                            confValues={this.state.confValues}
                            store={this._storesHub.poolAnalyticsStore}
                            isHosted={this.state.isHosted}
                            isPublic={true}
                            onDataRendered={this._onPublicTrendRendered}
                        />
                        <PoolTrendCard
                            className="flex-column margin-top-16"
                            confValues={this.state.confValues}
                            store={this._storesHub.poolAnalyticsStore}
                            isHosted={this.state.isHosted}
                            isPublic={false}
                            onDataRendered={this._onPrivateTrendRendered}
                        />
                        {/*
                         This code is the start of work for v2. Currently commented out for v1
                         <TopPipelinesCard
                            className="flex-column margin-top-16"
                            store={this._TopPipelinesStore}
                            isHosted={this.props.isHosted}
                            isPublic={true}
                            onDataRendered={this._onTopPipelinesRendered}
                        />
                        <TopPipelinesCard
                            className="flex-column margin-top-16"
                            store={this._TopPipelinesStore}
                            isHosted={this.props.isHosted}
                            isPublic={false}
                            onDataRendered={this._onTopPipelinesRendered}
                        /> */}
                    </div>
                </AnalyticsPage>
            </div>
        );
    }

    // get data validation promises for new selection value
    private filterChanged = () => {
        this._getDataValidationPromises();         
    }

    private _renderHeader = (): JSX.Element => {
        const pageData = this.context.pageContext.getService<IVssPageService>("IVssPageService").getData()!;

        const items: IHeaderCommandBarItem[] = [];
        if(pageData.isHosted) {
            const billingUrl = routeUrl(this.context.pageContext, "ms.vss-admin-web.collection-admin-hub-route", {adminPivot: "billing"});
            items.push(
                {
                    id: Resources.PurchaseConcurrencyButtonId,
                    isPrimary: true,
                    text: Resources.PurchaseConcurrency,
                    href: billingUrl,
                    onActivate: () => this._logButtonClick(Resources.PurchaseConcurrencyButtonId)
                }
            )
        }

        items.push(getFilterItem(() => {
            this._showFilter.value = !this._showFilter.value;
        }, this._filter.hasChangesToReset()))

        return (
            <AnalyticsHeaderComponent
                title={this.props.headerTitle}
                showCommandBar={true}
                commandBarItems={items}
            />
        );
    };

    private _getDataValidationPromises = (): Promise<any>[] | undefined => {
        const period = this._periods[this._timeRangeSelection.value[0].beginIndex];
        let trendBy: TrendBy;
        switch (period.data) {
            case Period.Days_1:
                trendBy = TrendBy.Hours;
                break;
            case Period.Days_180:
                trendBy = TrendBy.Weeks;
                break;
            default:
                trendBy = TrendBy.Days;
        }

        const confValues = {
            period: period.data,
            trendBy: trendBy
        } as ITrendReportConfiguration;
        this.setState({ ...this.state, confValues: confValues });
        return this._actionsCreator.updateConfigurationValues(confValues);
    };

    private _handleError = (errorText: string) => {
        this.setState({ errorText: errorText });
    };

    private _onChanged = (): void => {
        this.setState({ ...this.state, isHosted: this.props.storesHub.selectionStore.state.selectedAgentPool?.isHosted! });
    };

    private _onPrivateTrendRendered = () => {
        this._hasPrivateTrendRendered = true;
        this._logPerformance();
    };

    private _onPublicTrendRendered = () => {
        this._hasPublicTrendRendered = true;
        this._logPerformance();
    };

    private _onTopPipelinesRendered = () => {
        this._hasTopPipelinesRendered = true;
        this._logPerformance();
    };

    private _logPerformance() {
        if (this._hasPrivateTrendRendered && this._hasPublicTrendRendered) {
            if (this._reportRenderCount === 0) {
                const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
                perfService.markTimeToInteractive(Telemetry.Area, Telemetry.AgentPoolAnalytics);
                this._reportRenderCount++;
            }
        }
    }

    private _logButtonClick(id: string) {
        const perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        perfService.markTimeToInteractive(Telemetry.Area, Telemetry.AgentPoolAnalytics, undefined, {
            buttonAction: id
        });
    }
}
